/**
 * SecurityDashboard Page
 * Admin dashboard for security metrics and quick links to filtered audit views
 */

import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { auditApi } from '../../services/auditApi';
import { SecurityMetricCard } from '../../components/audit';
import { Button } from '../../components/ui';
import { pageStyles, cardStyles } from '../../lib/styles';
import type { SecurityMetrics } from '@leal-crm/shared';

export function SecurityDashboardPage() {
  const { user } = useAuth();
  const { error: showError } = useToast();

  // State
  const [metrics, setMetrics] = useState<SecurityMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Admin-only access
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const fetchMetrics = async () => {
    try {
      setIsLoading(true);
      const result = await auditApi.getMetrics();
      setMetrics(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar métricas';
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Format last updated timestamp
   */
  const formatLastUpdated = (timestampSeconds: number): string => {
    const date = new Date(timestampSeconds * 1000);
    return date.toLocaleString();
  };

  // Build filter URLs for linking to audit logs
  const buildFilterUrl = (eventTypes: string[]): string => {
    const params = new URLSearchParams();
    params.set('eventTypes', eventTypes.join(','));
    // Default to last 24 hours
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    params.set('startDate', yesterday.toISOString().split('T')[0]);
    return `/admin/audit?${params.toString()}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No se pudieron cargar las métricas</p>
        <Button onClick={fetchMetrics} className="mt-4">
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className={pageStyles.container}>
      {/* Header */}
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Panel de Seguridad</h1>
          <p className={pageStyles.subtitle}>
            Métricas de seguridad y autenticación (últimas 24 horas)
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-400">
            Actualizado: {formatLastUpdated(metrics.lastUpdated)}
          </span>
          <Button variant="secondary" size="sm" onClick={fetchMetrics}>
            Actualizar
          </Button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SecurityMetricCard
          title="Inicios Exitosos"
          value={metrics.successfulLogins24h}
          description="Últimas 24 horas"
          icon="✅"
          color="green"
          linkTo={buildFilterUrl(['login_success_password', 'login_success_passkey'])}
        />
        <SecurityMetricCard
          title="Intentos Fallidos"
          value={metrics.failedLogins24h}
          description="Últimas 24 horas"
          icon="❌"
          color={metrics.failedLogins24h > 10 ? 'red' : 'yellow'}
          linkTo={buildFilterUrl(['login_failed_password', 'login_failed_passkey'])}
        />
        <SecurityMetricCard
          title="Cuentas Bloqueadas"
          value={metrics.lockedAccounts24h}
          description="Últimas 24 horas"
          icon="🔒"
          color={metrics.lockedAccounts24h > 0 ? 'red' : 'gray'}
          linkTo={buildFilterUrl(['login_locked_out'])}
        />
        <SecurityMetricCard
          title="Sesiones Activas"
          value={metrics.activeSessions}
          description="Ahora mismo"
          icon="👥"
          color="blue"
        />
      </div>

      {/* Quick Actions */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Acciones Rápidas</span>
        </div>
        <div className={cardStyles.content}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/admin/audit"
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
            >
              <span className="text-xl">📋</span>
              <div>
                <p className="font-medium text-gray-900">Ver Todos los Logs</p>
                <p className="text-xs text-gray-500">Historial completo de auditoría</p>
              </div>
            </Link>
            <Link
              to={buildFilterUrl(['login_failed_password', 'login_failed_passkey', 'login_locked_out'])}
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
            >
              <span className="text-xl">⚠️</span>
              <div>
                <p className="font-medium text-gray-900">Eventos de Seguridad</p>
                <p className="text-xs text-gray-500">Fallos y bloqueos recientes</p>
              </div>
            </Link>
            <Link
              to="/admin/staff"
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
            >
              <span className="text-xl">👤</span>
              <div>
                <p className="font-medium text-gray-900">Gestión de Personal</p>
                <p className="text-xs text-gray-500">Administrar usuarios y accesos</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Security Tips */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Estado de Seguridad</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-2">
            {metrics.failedLogins24h === 0 && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <span>✓</span>
                <span>Sin intentos de acceso fallidos en las últimas 24 horas</span>
              </div>
            )}
            {metrics.failedLogins24h > 0 && metrics.failedLogins24h <= 5 && (
              <div className="flex items-center gap-2 text-yellow-600 text-sm">
                <span>⚠</span>
                <span>Se detectaron {metrics.failedLogins24h} intento(s) de acceso fallido(s)</span>
              </div>
            )}
            {metrics.failedLogins24h > 5 && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <span>⚠</span>
                <span>Alto número de intentos fallidos: {metrics.failedLogins24h}. Revisar logs.</span>
              </div>
            )}
            {metrics.lockedAccounts24h > 0 && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <span>🔒</span>
                <span>{metrics.lockedAccounts24h} cuenta(s) bloqueada(s). Considere revisar y desbloquear si es necesario.</span>
              </div>
            )}
            {metrics.lockedAccounts24h === 0 && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <span>✓</span>
                <span>No hay cuentas bloqueadas</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
